import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, getFirestore, query, where } from '@firebase/firestore';
import { app } from '../../firebase.js';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faMagnifyingGlass,
  faUserSlash,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import defaultProfilePicture from '../../Images/defprofile.png';
import {
  filterSchoolFeeIncomeRecords,
  formatKes,
  formatShortDate,
  normalizeAmount,
  toDateValue
} from '../../utils/financeWorkflow';

const ATTENDANCE_COLLECTION = 'AttendaceReport';
const NAIROBI_TIME_ZONE = 'Africa/Nairobi';
const ACTIVE_WINDOW_DAYS = 30;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const formatDateTime = (value, fallback = '—') => {
  const dateValue = toDateValue(value);
  if (!dateValue) {
    return fallback;
  }

  return dateValue.toLocaleString('en-KE', {
    timeZone: NAIROBI_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const getDaysSince = (value) => {
  const dateValue = toDateValue(value);
  if (!dateValue) {
    return null;
  }

  return Math.floor((Date.now() - dateValue.getTime()) / DAY_IN_MS);
};

const formatRelativeDays = (daysSince) => {
  if (daysSince === null || daysSince === undefined) {
    return 'No activity recorded';
  }

  if (daysSince <= 0) {
    return 'Today';
  }

  if (daysSince === 1) {
    return '1 day ago';
  }

  return `${daysSince} days ago`;
};

const getCourseLabel = (course = {}) => {
  return String(course.mcourseName || course.courseName || course.name || course.title || '').trim();
};

const resolveLastLogin = (data = {}) => {
  const candidates = [
    data.androidLastLoginAt,
    data.lastLoginAt,
    data.androidLastLoginAtMs,
    data.lastLoginAtMs,
    data.lastActiveAt
  ];

  for (const candidate of candidates) {
    const dateValue = toDateValue(candidate);
    if (dateValue) {
      return dateValue;
    }
  }

  return null;
};

const normalizeAttendanceType = (record = {}) => {
  const sessionTitle = normalizeText(record.sessionTitle || record.className);
  const mode = normalizeText(record.mode || record.attendanceMode);
  const hasRoomLink = Boolean(String(record.roomlink || '').trim());
  const hasVirtualSignals = hasRoomLink
    || mode === 'online'
    || sessionTitle.includes('live')
    || sessionTitle.includes('virtual')
    || Boolean(record.startTime || record.endTime);
  const hasPhysicalSignals = sessionTitle === normalizeText('School Check-In')
    || sessionTitle.includes('school check')
    || mode === 'onsite'
    || Boolean(record.locationCheck);

  if (hasVirtualSignals && !hasPhysicalSignals) {
    return 'Virtual';
  }

  if (hasPhysicalSignals) {
    return 'Physical';
  }

  return null;
};

const getRecordTimeValue = (record) => {
  return record?.checkInTime?.getTime?.() || record?.checkOutTime?.getTime?.() || 0;
};

const pickLatestRecord = (records = []) => {
  return [...records].sort((left, right) => getRecordTimeValue(right) - getRecordTimeValue(left))[0] || null;
};

function ActiveStudentsNotPayingFees() {
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [courseFilter, setCourseFilter] = useState('All');
  const [activityFilter, setActivityFilter] = useState('All');
  const [students, setStudents] = useState([]);

  const fetchStudents = useCallback(async () => {
    setRefreshing(true);

    try {
      const [usersSnapshot, incomeSnapshot, coursesSnapshot, attendanceSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('userStatus', '==', 'Student'))),
        getDocs(collection(db, 'Income')),
        getDocs(collection(db, 'courses')),
        getDocs(collection(db, ATTENDANCE_COLLECTION))
      ]);

      const courseMap = {};
      coursesSnapshot.forEach((docItem) => {
        const data = docItem.data();
        const courseLabel = getCourseLabel(data);

        if (!courseLabel) {
          return;
        }

        courseMap[normalizeText(courseLabel)] = {
          label: courseLabel,
          price: normalizeAmount(data.mcoursePrice || data.coursePrice || data.price || data.fee),
          installment: normalizeAmount(data.feeInstallments || data.monthlyFee || data.monthlyInstallment)
        };
      });

      const incomeByStudent = {};
      filterSchoolFeeIncomeRecords(
        incomeSnapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data()
        }))
      ).forEach((payment) => {
        const studentId = payment.studentId || payment.userId;
        if (!studentId) {
          return;
        }

        if (!incomeByStudent[studentId]) {
          incomeByStudent[studentId] = [];
        }

        incomeByStudent[studentId].push({
          amount: normalizeAmount(payment.incomeAmount || payment.amount),
          date: toDateValue(payment.paymentDate || payment.timeCreated || payment.createdAt)
        });
      });

      const attendanceByStudent = {};
      attendanceSnapshot.docs.forEach((docItem) => {
        const data = docItem.data();
        const checkInTime = toDateValue(data.signInTime || data.startTime || data.createdAt);
        const checkOutTime = toDateValue(data.signOutTime || data.endTime);
        const studentId = data.userId || data.studentId || '';
        const attendanceType = normalizeAttendanceType(data);

        if (!attendanceType || !studentId || !checkInTime) {
          return;
        }

        if (!attendanceByStudent[studentId]) {
          attendanceByStudent[studentId] = [];
        }

        attendanceByStudent[studentId].push({
          id: docItem.id,
          studentId,
          attendanceType,
          checkInTime,
          checkOutTime,
          isActive: Boolean(checkInTime && !checkOutTime),
          sessionTitle: data.sessionTitle || data.className || 'Class Session',
          mode: data.mode || data.attendanceMode || (data.roomlink ? 'Online' : 'Onsite')
        });
      });

      const studentRows = [];

      usersSnapshot.forEach((docItem) => {
        const data = docItem.data();
        const userType = normalizeText(data.userType);
        const userStatus = normalizeText(data.userStatus);

        if (userType !== 'student' && userStatus !== 'student') {
          return;
        }

        const userId = data.userId || docItem.id;
        const courseName = String(data.course || '').trim();
        const course = courseMap[normalizeText(courseName)];

        if (!userId || !course || course.price <= 0) {
          return;
        }

        const payments = incomeByStudent[userId] || [];
        const totalPaid = payments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
        if (totalPaid > 0) {
          return;
        }

        const lastLoginDate = resolveLastLogin(data);
        const attendanceRecords = attendanceByStudent[userId] || [];
        const latestAttendance = pickLatestRecord(attendanceRecords);
        const latestAttendanceDate = latestAttendance?.checkInTime || latestAttendance?.checkOutTime || null;

        const attendanceDays = getDaysSince(latestAttendanceDate);
        const loginDays = getDaysSince(lastLoginDate);
        const attendanceRecent = attendanceDays !== null && attendanceDays <= ACTIVE_WINDOW_DAYS;
        const androidRecent = loginDays !== null && loginDays <= ACTIVE_WINDOW_DAYS;
        const openSession = attendanceRecords.some((record) => record.isActive);

        const activeSources = [];
        if (attendanceRecent || openSession) {
          activeSources.push('Attendance');
        }
        if (androidRecent) {
          activeSources.push('Android');
        }

        if (!activeSources.length) {
          return;
        }

        const lastActivityDate = [latestAttendanceDate, lastLoginDate]
          .filter(Boolean)
          .sort((first, second) => second.getTime() - first.getTime())[0] || null;

        const balance = Math.max(course.price - totalPaid, 0);
        const activityLabel = openSession
          ? 'Active today'
          : attendanceDays !== null && attendanceDays <= 1
            ? 'Active today'
            : attendanceDays !== null && attendanceDays <= 7
              ? 'Active this week'
              : attendanceDays !== null && attendanceDays <= ACTIVE_WINDOW_DAYS
                ? 'Active this month'
                : androidRecent && loginDays !== null && loginDays <= 1
                  ? 'Active today'
                  : androidRecent && loginDays !== null && loginDays <= 7
                    ? 'Active this week'
                    : 'Active this month';

        studentRows.push({
          id: docItem.id,
          userId,
          bio: data.bio || data.displayName || data.name || 'Unnamed student',
          course: course.label || courseName || 'Course not set',
          campus: data.branchName || data.campus || data.selectedCampus || '',
          email: data.userEmail || data.email || '',
          phoneNumber: data.phoneNumber || data.phone || '',
          profilePic: data.profilePic || defaultProfilePicture,
          admissionDate: toDateValue(data.admissionDate || data.appDate),
          lastActivityDate,
          latestAttendanceDate,
          lastLoginDate,
          lastPaymentDate: null,
          courseFee: course.price,
          monthlyInstallment: course.installment,
          totalPaid,
          balance,
          activityLabel,
          activeSources,
          attendanceRecent,
          androidRecent,
          openSession,
          attendanceCount: attendanceRecords.length,
          paymentCount: payments.length
        });
      });

      studentRows.sort((first, second) => {
        const firstTime = first.lastActivityDate?.getTime?.() || 0;
        const secondTime = second.lastActivityDate?.getTime?.() || 0;
        if (secondTime !== firstTime) {
          return secondTime - firstTime;
        }

        return String(first.bio || '').localeCompare(String(second.bio || ''));
      });

      setStudents(studentRows);
    } catch (error) {
      console.error('Failed to load active students not paying fees:', error);
      setStudents([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [db]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const filteredStudents = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return students.filter((student) => {
      const matchesCourse = courseFilter === 'All' || student.course === courseFilter;
      const matchesActivity = activityFilter === 'All' || (
        activityFilter === 'Attendance'
          ? student.activeSources.includes('Attendance') && !student.activeSources.includes('Android')
          : activityFilter === 'Android'
            ? student.activeSources.includes('Android') && !student.activeSources.includes('Attendance')
            : activityFilter === 'Both'
              ? student.activeSources.includes('Attendance') && student.activeSources.includes('Android')
              : true
      );

      if (!matchesCourse || !matchesActivity) {
        return false;
      }

      if (!search) {
        return true;
      }

      return [
        student.bio,
        student.course,
        student.campus,
        student.email,
        student.phoneNumber
      ].some((value) => String(value || '').toLowerCase().includes(search));
    });
  }, [activityFilter, courseFilter, searchTerm, students]);

  const uniqueCourses = useMemo(() => {
    return Array.from(new Set(students.map((student) => student.course).filter(Boolean))).sort();
  }, [students]);

  const stats = useMemo(() => {
    const breakdown = {
      attendance: 0,
      android: 0,
      both: 0,
      totalBalance: 0
    };

    students.forEach((student) => {
      if (student.activeSources.includes('Attendance')) {
        breakdown.attendance += 1;
      }

      if (student.activeSources.includes('Android')) {
        breakdown.android += 1;
      }

      if (student.activeSources.includes('Attendance') && student.activeSources.includes('Android')) {
        breakdown.both += 1;
      }

      breakdown.totalBalance += normalizeAmount(student.balance);
    });

    return breakdown;
  }, [students]);

  const viewStudent = (userId) => {
    navigate('/view-student', { state: { myData: userId } });
  };

  return (
    <div className="clientsComp applicationsPage">
      <LeftBar />
      <div className="dashMainContent">
        <Dashnav />

        <div className="appsHeader">
          <div>
            <p className="appsKicker">Data Analytics</p>
            <h4>Active Students Not Paying Fees</h4>
            <p className="appsSubtext">
              Students who look active from attendance or device activity, but have no school-fee payments recorded yet.
            </p>
          </div>
          <div className="appsHeaderStats">
            <div className="appsStatChip">
              <span>Total</span>
              <strong>{students.length}</strong>
            </div>
            <div className="appsStatChip">
              <span>Attendance-backed</span>
              <strong>{stats.attendance}</strong>
            </div>
            <div className="appsStatChip">
              <span>Android-backed</span>
              <strong>{stats.android}</strong>
            </div>
            <div className="appsStatChip">
              <span>Both signals</span>
              <strong>{stats.both}</strong>
            </div>
            <div className="appsStatChip">
              <span>Total balance</span>
              <strong>{formatKes(stats.totalBalance)}</strong>
            </div>
          </div>
        </div>

        <div className="appsFiltersCard">
          <div className="appsSearch input-group">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </span>
            <Form.Control
              type="text"
              placeholder="Search name, course, campus..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div className="appsFilterGroup">
            <Form.Select className="filterSelect" value={courseFilter} onChange={(event) => setCourseFilter(event.target.value)}>
              <option value="All">All Courses</option>
              {uniqueCourses.map((course) => (
                <option key={course} value={course}>
                  {course}
                </option>
              ))}
            </Form.Select>
            <Form.Select className="filterSelect" value={activityFilter} onChange={(event) => setActivityFilter(event.target.value)}>
              <option value="All">All Activity</option>
              <option value="Attendance">Attendance only</option>
              <option value="Android">Android only</option>
              <option value="Both">Both signals</option>
            </Form.Select>
          </div>
        </div>

        <div className="appsTableCard">
          <div className="appsTableHeader">
            <h6>
              <FontAwesomeIcon icon={faUserSlash} className="me-2" />
              Active Students Not Paying
            </h6>
            <span>{filteredStudents.length} results</span>
          </div>
          <div className="appsTableWrap">
            {loading ? (
              <div className="incomeStateCard">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading active students not paying fees...</span>
                </Spinner>
                <p>Loading active students not paying fees...</p>
              </div>
            ) : (
              <Table hover responsive className="appsTable">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Course</th>
                    <th>Activity</th>
                    <th>Attendance</th>
                    <th>Android Login</th>
                    <th>Total Paid</th>
                    <th>Balance Due</th>
                    <th>Signals</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student) => (
                    <tr key={student.id}>
                      <td>
                        <div className="appsStudentCell">
                          <div className="appDefProfPic">
                            <img src={student.profilePic || defaultProfilePicture} alt="profile" />
                          </div>
                          <div>
                            <p className="appsStudentName">{student.bio}</p>
                            <span className="appsStudentMeta">{student.email || student.phoneNumber || 'Student profile'}</span>
                          </div>
                        </div>
                      </td>
                      <td>{student.course || '—'}</td>
                      <td>
                        <div className="attendanceRecordCell">
                          <strong>{student.activityLabel}</strong>
                          <span>{student.openSession ? 'Open session' : `Last activity ${formatRelativeDays(getDaysSince(student.lastActivityDate))}`}</span>
                        </div>
                      </td>
                      <td>
                        <div className="attendanceRecordCell">
                          <strong>{formatDateTime(student.latestAttendanceDate, 'No attendance record')}</strong>
                          <span>{student.attendanceRecent ? 'Recent attendance' : 'Older attendance'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="attendanceRecordCell">
                          <strong>{formatDateTime(student.lastLoginDate, 'No Android login')}</strong>
                          <span>{student.androidRecent ? 'Recent Android login' : 'Older Android login'}</span>
                        </div>
                      </td>
                      <td>{formatKes(student.totalPaid)}</td>
                      <td>{formatKes(student.balance)}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {student.activeSources.includes('Attendance') && (
                            <Badge bg="success">Attendance</Badge>
                          )}
                          {student.activeSources.includes('Android') && (
                            <Badge bg="info">Android</Badge>
                          )}
                          {student.openSession && (
                            <Badge bg="warning" text="dark">Open session</Badge>
                          )}
                        </div>
                        <small className="d-block text-muted mt-1">
                          {student.attendanceCount} attendance record{student.attendanceCount === 1 ? '' : 's'}
                          {' '}• {student.paymentCount} payment{student.paymentCount === 1 ? '' : 's'}
                        </small>
                      </td>
                      <td>
                        <button className="appsViewBtn" onClick={() => viewStudent(student.userId)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredStudents.length === 0 && (
                    <tr>
                      <td colSpan="9" className="appsEmptyState">
                        No active students without school fee payments match the selected filters yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>
          <div className="d-flex justify-content-end mt-3">
            <Button variant="outline-dark" onClick={fetchStudents} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ActiveStudentsNotPayingFees;
